import React, { useState, useEffect } from 'react';
import { GiftIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon, ClockIcon, DocumentArrowDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ReimbursementBonusRules = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [activeTab, setActiveTab] = useState('rules');
    const [activityLogs, setActivityLogs] = useState([]);
    const [reportType, setReportType] = useState('daily');
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [formData, setFormData] = useState({
        bonus_code: '',
        bonus_name: '',
        frequency: '',
        fixed_amount: ''
    });

    useEffect(() => {
        fetchRules();
        fetchActivityLogs();
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            setError(null);
            const empCode = localStorage.getItem('emp_code') || 'EMP8383';
            
            const response = await fetch('http://localhost:8000/rules/bonus/bonus-rules/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-EMP-CODE': empCode
                }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const rulesList = Array.isArray(data) ? data : (data?.data || []);
            setRules(rulesList);
        } catch (error) {
            console.error('❌ Failed to fetch rules:', error);
            setError(error.message);
            setRules([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (rule = null) => {
        if (rule) {
            setEditingRule(rule);
            setFormData({
                bonus_code: rule.bonus_code,
                bonus_name: rule.bonus_name,
                frequency: rule.frequency,
                fixed_amount: rule.fixed_amount || ''
            });
        } else {
            setEditingRule(null);
            setFormData({ bonus_code: '', bonus_name: '', frequency: '', fixed_amount: '' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingRule(null);
    };

    const handleSave = async () => {
        try {
            const empCode = localStorage.getItem('emp_code') || 'EMP8383';
            const payload = {
                bonusCode: formData.bonus_code,
                bonusName: formData.bonus_name,
                frequency: formData.frequency,
                fixedAmount: formData.fixed_amount ? parseFloat(formData.fixed_amount) : null
            };

            const url = editingRule 
                ? `http://localhost:8000/rules/bonus/bonus-rules/${editingRule.id}`
                : 'http://localhost:8000/rules/bonus/bonus-rules/';
            
            const method = editingRule ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-EMP-CODE': empCode
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            await fetchRules();
            handleCloseModal();
            alert(editingRule ? 'Rule updated successfully!' : 'Rule created successfully!');
        } catch (error) {
            console.error('❌ Save failed:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const handleDelete = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this rule?')) return;

        try {
            const empCode = localStorage.getItem('emp_code') || 'EMP8383';
            const response = await fetch(`http://localhost:8000/rules/bonus/bonus-rules/${ruleId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-EMP-CODE': empCode
                }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            await fetchRules();
            await fetchActivityLogs();
            alert('Rule deleted successfully!');
        } catch (error) {
            console.error('❌ Delete failed:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const fetchActivityLogs = async () => {
        try {
            // Mock data - replace with actual API call
            const mockLogs = [
                { id: 1, employeeId: 'EMP8383', action: 'Created', modifiedBy: 'Admin User', date: '2024-01-15', details: 'Created new bonus rule: Performance Bonus' },
                { id: 2, employeeId: 'EMP8384', action: 'Updated', modifiedBy: 'HR Manager', date: '2024-01-14', details: 'Updated frequency for Annual Bonus' },
                { id: 3, employeeId: 'EMP8385', action: 'Deleted', modifiedBy: 'System Admin', date: '2024-01-13', details: 'Deleted inactive bonus rule' }
            ];
            setActivityLogs(mockLogs);
        } catch (error) {
            console.error('Failed to fetch activity logs:', error);
        }
    };

    const downloadExcel = (data, filename) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, `${filename}.xlsx`);
    };

    const downloadPDF = (data, filename, title) => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(title, 20, 20);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
        
        const tableData = data.map(item => Object.values(item));
        const headers = Object.keys(data[0] || {});
        
        doc.autoTable({
            head: [headers],
            body: tableData,
            startY: 40
        });
        
        doc.save(`${filename}.pdf`);
    };

    const generateReport = () => {
        let reportData = [];
        let filename = '';
        let title = '';
        
        if (activeTab === 'rules') {
            reportData = rules.map(rule => ({
                'Bonus Code': rule.bonus_code,
                'Bonus Name': rule.bonus_name,
                'Frequency': rule.frequency,
                'Fixed Amount': rule.fixed_amount || 0,
                'Status': rule.is_active ? 'Active' : 'Inactive'
            }));
            filename = `reimbursement-bonus-rules-${reportType}-${new Date().toISOString().split('T')[0]}`;
            title = `Reimbursement & Bonus Rules Report - ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        } else if (activeTab === 'logs') {
            reportData = activityLogs.map(log => ({
                'Employee ID': log.employeeId,
                'Action': log.action,
                'Modified By': log.modifiedBy,
                'Date': log.date,
                'Details': log.details
            }));
            filename = `activity-logs-${reportType}-${new Date().toISOString().split('T')[0]}`;
            title = `Activity Logs Report - ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        }
        
        return { reportData, filename, title };
    };

    const handleDownload = (format) => {
        const { reportData, filename, title } = generateReport();
        if (reportData.length === 0) {
            alert('No data available for download');
            return;
        }
        
        if (format === 'excel') {
            downloadExcel(reportData, filename);
        } else if (format === 'pdf') {
            downloadPDF(reportData, filename, title);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50" style={{ fontFamily: 'Roboto, sans-serif' }}>
            <header className="pb-6 mb-8 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <GiftIcon className="w-8 h-8 inline-block mr-2 text-green-600"/> Reimbursement & Bonus Rules
                    </h1>
                    <p className="text-sm text-gray-500 mt-2">Manage company bonus and reimbursement rules</p>
                </div>
                {activeTab === 'rules' && (
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                        <PlusIcon className="w-5 h-5" /> Add Rule
                    </button>
                )}
            </header>

            {/* Tabs */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('rules')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'rules'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <GiftIcon className="w-5 h-5 inline-block mr-2" />
                            Rules
                        </button>
                        <button
                            onClick={() => setActiveTab('logs')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'logs'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <ClockIcon className="w-5 h-5 inline-block mr-2" />
                            Activity Logs
                        </button>
                        <button
                            onClick={() => setActiveTab('reports')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'reports'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <ChartBarIcon className="w-5 h-5 inline-block mr-2" />
                            Reports
                        </button>
                    </nav>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800"><strong>Error:</strong> {error}</p>
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                {/* Rules Tab */}
                {activeTab === 'rules' && (
                    <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Bonus Code', 'Bonus Name', 'Frequency', 'Fixed Amount', 'Status', 'Actions'].map(header => (
                                        <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {rules.length > 0 ? (
                                    rules.map((rule) => (
                                        <tr key={rule.id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4 font-medium text-gray-900">{rule.bonus_code}</td>
                                            <td className="px-6 py-4 font-medium text-gray-900">{rule.bonus_name}</td>
                                            <td className="px-6 py-4 text-gray-700">{rule.frequency}</td>
                                            <td className="px-6 py-4 text-indigo-600 font-semibold">₹{rule.fixed_amount || '-'}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${rule.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {rule.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 flex gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(rule)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                                                    title="Edit"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(rule.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No bonus rules found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Activity Logs Tab */}
                {activeTab === 'logs' && (
                    <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Employee ID', 'Action', 'Modified By', 'Date', 'Details'].map(header => (
                                        <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {activityLogs.length > 0 ? (
                                    activityLogs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 transition">
                                            <td className="px-6 py-4 font-medium text-gray-900">{log.employeeId}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    log.action === 'Created' ? 'bg-green-100 text-green-800' :
                                                    log.action === 'Updated' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">{log.modifiedBy}</td>
                                            <td className="px-6 py-4 text-gray-700">{log.date}</td>
                                            <td className="px-6 py-4 text-gray-700">{log.details}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">No activity logs found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                    <div className="bg-white shadow-lg rounded-xl p-6">
                        <h3 className="text-lg font-semibold mb-4">Generate Reports</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                                <select
                                    value={reportType}
                                    onChange={(e) => setReportType(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                >
                                    <option value="daily">Daily</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="half-yearly">Half Yearly</option>
                                    <option value="yearly">Yearly</option>
                                    <option value="custom">Custom Date Range</option>
                                </select>
                            </div>
                            
                            {reportType === 'custom' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                        <input
                                            type="date"
                                            value={customDateRange.start}
                                            onChange={(e) => setCustomDateRange({...customDateRange, start: e.target.value})}
                                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                        <input
                                            type="date"
                                            value={customDateRange.end}
                                            onChange={(e) => setCustomDateRange({...customDateRange, end: e.target.value})}
                                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-4">
                            <button
                                onClick={() => handleDownload('excel')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                                <DocumentArrowDownIcon className="w-5 h-5" />
                                Download Excel
                            </button>
                            <button
                                onClick={() => handleDownload('pdf')}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                            >
                                <DocumentArrowDownIcon className="w-5 h-5" />
                                Download PDF
                            </button>
                        </div>
                        
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Report Preview</h4>
                            <p className="text-sm text-gray-600">
                                Current tab: <span className="font-semibold">{activeTab === 'rules' ? 'Rules' : 'Activity Logs'}</span><br/>
                                Report type: <span className="font-semibold">{reportType.charAt(0).toUpperCase() + reportType.slice(1)}</span><br/>
                                {reportType === 'custom' && customDateRange.start && customDateRange.end && (
                                    <>Date range: <span className="font-semibold">{customDateRange.start} to {customDateRange.end}</span><br/></>
                                )}
                                Records: <span className="font-semibold">{activeTab === 'rules' ? rules.length : activityLogs.length}</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{editingRule ? 'Edit Rule' : 'Add New Rule'}</h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <input
                                type="text"
                                placeholder="Bonus Code"
                                value={formData.bonus_code}
                                onChange={(e) => setFormData({...formData, bonus_code: e.target.value})}
                                className="w-full border rounded px-3 py-2 text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Bonus Name"
                                value={formData.bonus_name}
                                onChange={(e) => setFormData({...formData, bonus_name: e.target.value})}
                                className="w-full border rounded px-3 py-2 text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Frequency (e.g., Monthly, Annually)"
                                value={formData.frequency}
                                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                                className="w-full border rounded px-3 py-2 text-sm"
                            />
                            <input
                                type="number"
                                placeholder="Fixed Amount (₹)"
                                value={formData.fixed_amount}
                                onChange={(e) => setFormData({...formData, fixed_amount: e.target.value})}
                                className="w-full border rounded px-3 py-2 text-sm"
                            />
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 px-4 py-2 border rounded hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                            >
                                {editingRule ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReimbursementBonusRules;
